import {
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';

import type {
  CreateAgentResponse,
  GetAgentByIdResponse,
  GetAgentsRequest,
  GetAgentsResponse,
  UpdateAgentBody,
  UpdateAgentResponse,
} from '@mai/platform-api/src/routes/agents.schema';
import type { Agent } from '@mai/types';

import { type QueryOptions, apiClient } from './axios';

// Type definitions from platform API schemas
export type GetAgentsRequestType = GetAgentsRequest;
export type CreateAgentRequest = {
  teamId: string;
  name: string;
  description?: string | null;
};
export type UpdateAgentRequest = UpdateAgentBody;
export type { Agent, CreateAgentResponse, UpdateAgentResponse };

// API functions
export const fetchAgents = async (
  params?: GetAgentsRequestType,
): Promise<GetAgentsResponse> => {
  const response = await apiClient.get('/agents', { params });
  return response.data;
};

export const fetchAgentsByTeamId = async (
  teamId: string,
): Promise<GetAgentsResponse> => {
  return fetchAgents({ teamIds: [teamId] });
};

export const fetchAgentById = async (
  agentId: string,
): Promise<GetAgentByIdResponse> => {
  const response = await apiClient.get(`/agents/${agentId}`);
  return response.data;
};

export const createAgent = async (
  data: CreateAgentRequest,
): Promise<CreateAgentResponse> => {
  const response = await apiClient.post('/agents', data);
  return response.data;
};

export const updateAgent = async (
  agentId: string,
  data: UpdateAgentRequest,
): Promise<UpdateAgentResponse> => {
  const response = await apiClient.patch(`/agents/${agentId}`, data);
  return response.data;
};

export const deleteAgent = async (agentId: string): Promise<void> => {
  await apiClient.delete(`/agents/${agentId}`);
};

// React Query hooks
export const useAgents = (
  params?: GetAgentsRequestType,
  options?: QueryOptions,
) => {
  return useQuery({
    queryKey: ['agents', params],
    queryFn: () => fetchAgents(params),
    enabled: options?.enabled,
    placeholderData: keepPreviousData,
    retry: options?.retry,
  });
};

export const useAgentsByTeamId = (
  teamId: string | null,
  options?: QueryOptions,
) => {
  return useQuery({
    queryKey: ['agents', 'by-team', teamId],
    queryFn: () => fetchAgentsByTeamId(teamId!),
    enabled: !!teamId && options?.enabled !== false,
    placeholderData: keepPreviousData,
    retry: options?.retry,
  });
};

export const useAgentById = (
  agentId: string | null,
  options?: QueryOptions,
) => {
  return useQuery({
    queryKey: ['agents', 'by-id', agentId],
    queryFn: () => fetchAgentById(agentId!),
    enabled: !!agentId && options?.enabled !== false,
    retry: options?.retry,
  });
};

export const useCreateAgent = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: createAgent,
    onSuccess: () => {
      // Invalidate all agents queries to refetch the data
      void queryClient.invalidateQueries({ queryKey: ['agents'] });
    },
  });
};

export const useUpdateAgent = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      agentId,
      data,
    }: {
      agentId: string;
      data: UpdateAgentRequest;
    }) => updateAgent(agentId, data),
    onSuccess: () => {
      // Invalidate all agents queries to refetch the data
      void queryClient.invalidateQueries({ queryKey: ['agents'] });
    },
  });
};

export const useDeleteAgent = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: deleteAgent,
    onSuccess: () => {
      // Invalidate all agents queries to refetch the data
      void queryClient.invalidateQueries({ queryKey: ['agents'] });
    },
  });
};
